/**
 * Portal header dropdown to switch between engagements when the contact's
 * client runs more than one concurrently. Hidden when there's only one.
 *
 * State lives in localStorage via useActiveEngagement. Switching triggers
 * a full reload so all React Query caches drop and refetch under the new
 * engagement id.
 */
import { useEffect, useRef, useState } from 'react';
import { useActiveEngagement } from '../../hooks/useActiveEngagement.js';

const TYPE_LABELS = {
  RETAINER: 'Retainer',
  PROJECT: 'Project',
  ADVISORY: 'Advisory',
} as const;

const STATUS_LABELS = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
} as const;

export function EngagementSwitcher() {
  const { engagement, engagements, setActiveEngagementId } = useActiveEngagement();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  if (engagements.length <= 1) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 items-center gap-2 rounded-lg border border-scope-border bg-white px-3 text-sm text-scope-navy hover:bg-scope-cream-warm"
      >
        <span className="font-medium">{engagement?.name ?? 'Select engagement'}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-72 overflow-hidden rounded-lg border border-scope-border bg-white shadow-scope">
          <div className="border-b border-scope-border-soft px-3 py-2 text-2xs uppercase tracking-wider text-scope-text-muted">
            Switch engagement
          </div>
          <ul>
            {engagements.map((e) => {
              const active = engagement?.id === e.id;
              return (
                <li key={e.id}>
                  <button
                    onClick={() => {
                      setActiveEngagementId(e.id);
                      setOpen(false);
                      window.location.assign('/portal');
                    }}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm transition hover:bg-scope-cream-warm ${
                      active ? 'text-scope-navy' : 'text-scope-text-muted'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className={`truncate ${active ? 'font-semibold text-scope-navy' : ''}`}>
                        {e.name}
                      </div>
                      <div className="mt-0.5 text-2xs text-scope-text-faint">
                        {TYPE_LABELS[e.type]} · {STATUS_LABELS[e.status] ?? e.status}
                      </div>
                    </div>
                    {active && (
                      <span className="rounded bg-scope-cream-warm px-1.5 py-0.5 text-2xs font-medium text-scope-navy">
                        Active
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
